import * as React from 'react'
import { useMemo, useRef, useState, useEffect } from 'react'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { useQueryCountriesSettings } from '@/lib/api/company/configuration/countries/useQueryCountriesSettings'

import { DropdownPortal } from '@/modules/settings/components/DropdownPortal'
import { CountryModel } from '@/modules/shared/types/country'
import { CountryFlag } from '@/modules/shared/components/CountryFlag'
import { Checkbox } from '@/ui'

type Props = {
  companyId: string
  title?: string
  readOnly?: boolean
  max?: number
  errorMsg?: string
  selectedCountries?: CountryModel[]
  setSelectedCountries?: (selectedCountries: CountryModel[]) => void
}

export const CompanyCountriesSection: React.FC<Props> = ({
  companyId,
  title,
  readOnly,
  max,
  errorMsg,
  selectedCountries,
  setSelectedCountries,
}) => {
  const { t } = useLocalize()
  const q = useQueryCountriesSettings({ companyId })
  const buttonRef = React.useRef<HTMLButtonElement | null>(null)

  // --- State management: allow external control or fall back to internal state
  const [internalSelected, setInternalSelected] = useState<CountryModel[]>([])
  const selected = selectedCountries ?? internalSelected

  const setSelected = (items: CountryModel[]) => {
    if (setSelectedCountries) {
      setSelectedCountries(items)
    } else {
      setInternalSelected(items)
    }
  }

  // --- Dropdown open/close
  const [open, setOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement | null>(null)

  // --- Search
  const [query, setQuery] = useState('')

  // --- Close dropdown on outside click
  useEffect(() => {
    // Close on outside click
    const onClick = (e: MouseEvent) => {
      if (!dropdownRef.current) return
      if (!dropdownRef.current.contains(e.target as Node)) setOpen(false)
    }
    // Close on Escape
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [])

  // --- Derive countries list
  const allCountries = useMemo<CountryModel[]>(
    () => (q.data ?? []).slice().sort((a, b) => a.name.localeCompare(b.name)),
    [q.data],
  )

  // --- Filter by search phrase
  const filtered = useMemo(() => {
    const p = query.trim().toLowerCase()
    if (!p) return allCountries
    return allCountries.filter(
      (c) =>
        c.name.toLowerCase().includes(p) || c.code.toLowerCase().includes(p),
    )
  }, [allCountries, query])

  // --- Split into selected / available with selected pinned on top
  const { selectedList, availableList } = useMemo(() => {
    const selIds = new Set(selected.map((s) => s.id))
    const selectedList = filtered.filter((c) => selIds.has(c.id))
    const availableList = filtered.filter((c) => !selIds.has(c.id))
    return { selectedList, availableList }
  }, [filtered, selected])

  // --- Helpers
  const isSelected = (id: number) => selected.some((c) => c.id === id)

  const toggleCountry = (country: CountryModel) => {
    // Respect readOnly
    if (readOnly) return

    const exists = isSelected(country.id)
    if (exists) {
      setSelected(selected.filter((c) => c.id !== country.id))
      return
    }
    // Respect max if provided
    if (typeof max === 'number' && selected.length >= max) {
      return
    }
    setSelected([...selected, country])
  }

  const removeCountry = (id: number) => {
    if (readOnly) return
    setSelected(selected.filter((c) => c.id !== id))
  }

    // --- UI text (can be localized with t if masz klucze)
    const labelTitle = title ?? t('configuration-businessTrip.countries');
    const labelSearch = t('configuration-businessTrip.search-country');
    const labelSelected = t('common.selected');
    const labelAvailable = t('configuration-businessTrip.available');
    const labelAdd = selected.length ? `${t('configuration-businessTrip.add-countries')} (${selected.length} ${t('common.selected').toLowerCase()})` : t('configuration-businessTrip.add-countries');

  // --- Loading / error states
  if (q.isLoading) {
    return (
      <div className="tw-w-full tw-rounded-2xl tw-border tw-border-gray-200 tw-bg-white tw-p-4">
        <div className="tw-h-5 tw-w-48 tw-animate-pulse tw-rounded tw-bg-gray-200" />
        <div className="tw-mt-4 tw-flex tw-flex-wrap tw-gap-2">
          <div className="tw-h-8 tw-w-24 tw-animate-pulse tw-rounded-full tw-bg-gray-200" />
          <div className="tw-h-8 tw-w-28 tw-animate-pulse tw-rounded-full tw-bg-gray-200" />
        </div>
      </div>
    )
  }

  if (q.isError) {
    return (
      <div className="tw-w-full tw-rounded-2xl tw-border tw-border-red-200 tw-bg-red-50 tw-p-4 tw-text-red-800">
        Failed to load countries configuration.
      </div>
    )
  }

  return (
    <section className="tw-w-full tw-rounded-2xl tw-border tw-border-gray-200 tw-bg-white tw-p-4 tw-shadow-sm">
      {/* Header */}
      <div className="tw-mb-3 tw-flex tw-items-center tw-justify-between">
        <h3 className="tw-text-lg tw-font-semibold tw-text-gray-900">
          {labelTitle}
        </h3>
        {typeof max === 'number' && (
          <span
            className={`tw-text-xs ${errorMsg ? 'tw-text-red-600' : 'tw-text-gray-500'}`}
          >
            {selected.length}/{max}
          </span>
        )}
      </div>

      {errorMsg && (
        <div className="tw-mb-3 tw-text-sm tw-text-red-600">{errorMsg}</div>
      )}

            {/* Selected chips */}
            <div className="tw-flex tw-flex-wrap tw-gap-2">
                {selected.length === 0 && (
                    <span className="tw-text-sm tw-text-gray-500">{t('configuration-businessTrip.no-country-selected')}</span>
                )}
                {selected.map((c) => (
                    <span
                        key={c.id}
                        className="tw-inline-flex tw-items-center tw-gap-2 tw-rounded-full tw-border tw-border-gray-300 tw-bg-gray-50 tw-px-3 tw-py-1 tw-text-sm tw-text-gray-800"
                        title={`${c.name} (${c.code})`}
                    >
                        <CountryFlag code={c.code} />
                        <span className="tw-inline-block tw-font-medium">{c.name}</span>
                        {c?.code && (
                            <span className="tw-inline-block tw-text-xs tw-text-gray-500">{c.code}</span>
                        )}
                        {!readOnly && (
                            <button
                                type="button"
                                onClick={() => removeCountry(c.id)}
                                className="tw-rounded-full tw-p-1 hover:tw-bg-gray-200 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-gray-300"
                                aria-label={`Remove ${c.name}`}
                                title="Remove"
                            >
                                ×
                            </button>
                        )}
                    </span>
                ))}
            </div>

      {/* Dropdown */}
      <div className="tw-relative tw-mt-4" ref={dropdownRef}>
        <button
          ref={buttonRef}
          type="button"
          disabled={
            readOnly ||
            (typeof max === 'number' &&
              selected.length >= max &&
              availableList.length === 0)
          }
          onClick={() => setOpen((o) => !o)}
          className={[
            'tw-inline-flex tw-w-full tw-items-center tw-justify-between tw-rounded-xl tw-border tw-px-3 tw-py-2 tw-text-sm',
            readOnly
              ? 'tw-cursor-not-allowed tw-border-gray-200 tw-bg-gray-50 tw-text-gray-400'
              : 'tw-border-gray-300 tw-bg-white hover:tw-border-gray-400',
          ].join(' ')}
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          <span className="tw-truncate">{labelAdd}</span>
          <svg
            className={`tw-h-4 tw-w-4 tw-transition-transform ${open ? 'tw-rotate-180' : ''}`}
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z" />
          </svg>
        </button>

        <DropdownPortal
          open={open}
          anchorEl={buttonRef.current}
          onClose={() => setOpen(false)}
          // optional: minWidth dopasuje szerokość do guzika, albo ustaw własną
          // minWidth={360}
        >
          <div
            className="tw-absolute tw-z-20 tw-mt-2 tw-max-h-80 tw-w-full tw-overflow-hidden tw-rounded-xl tw-border tw-border-gray-200 tw-bg-white tw-shadow-lg"
            role="dialog"
          >
            {/* Search */}
            <div className="tw-border-b tw-border-gray-200 tw-p-2">
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={labelSearch}
                className="tw-w-full tw-rounded-lg tw-border tw-border-gray-300 tw-px-3 tw-py-2 tw-text-sm focus:tw-border-gray-400 focus:tw-outline-none"
              />
            </div>

            {/* List */}
            <div
              className="tw-max-h-64 tw-overflow-auto tw-p-1"
              role="listbox"
              aria-multiselectable="true"
            >
              {/* Selected group */}
              {selectedList.length > 0 && (
                <Group
                  title={labelSelected}
                  items={selectedList}
                  isSelected={isSelected}
                  onToggle={toggleCountry}
                  readOnly={!!readOnly}
                  max={max}
                  selectedCount={selected.length}
                />
              )}
              {/* Available group */}
              <Group
                title={labelAvailable}
                items={availableList}
                isSelected={isSelected}
                onToggle={toggleCountry}
                readOnly={!!readOnly}
                max={max}
                selectedCount={selected.length}
              />
            </div>

            {/* Footer info */}
            <div className="tw-flex tw-items-center tw-justify-between tw-border-t tw-border-gray-200 tw-bg-gray-50 tw-px-3 tw-py-2">
              <span className="tw-text-xs tw-text-gray-500">
                {filtered.length} results
              </span>
              <div className="tw-flex tw-gap-2">
                <button
                  type="button"
                  className="tw-rounded-lg tw-border tw-border-gray-300 tw-bg-white tw-px-2 tw-py-1 tw-text-xs hover:tw-border-gray-400"
                  onClick={() => setOpen(false)}
                >
                  Close
                </button>
                {!readOnly && (
                  <button
                    type="button"
                    className="tw-rounded-lg tw-bg-gray-900 tw-px-2 tw-py-1 tw-text-xs tw-text-white hover:tw-bg-black"
                    onClick={() => setOpen(false)}
                  >
                    Done
                  </button>
                )}
              </div>
            </div>
          </div>
        </DropdownPortal>
      </div>
    </section>
  )
}

type GroupProps = {
  title: string
  items: CountryModel[]
  isSelected: (id: number) => boolean
  onToggle: (c: CountryModel) => void
  readOnly: boolean
  max?: number
  selectedCount: number
}

const Group: React.FC<GroupProps> = ({
  title,
  items,
  isSelected,
  onToggle,
  readOnly,
  max,
  selectedCount,
}) => {
  // Determine if selection should be disabled because max reached
  const maxReached = typeof max === 'number' && selectedCount >= max

  if (items.length === 0) return null

  return (
    <div className="tw-py-1">
      <div className="tw-sticky tw-top-0 tw-z-10 tw-bg-white tw-px-2 tw-py-1">
        <div className="tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wider tw-text-gray-500">
          {title}
        </div>
      </div>
      <ul className="tw-space-y-0.5">
        {items.map((c) => {
          const checked = isSelected(c.id)
          const disabled = readOnly || (!checked && maxReached)
          const id = `country-${c.id}-checkbox`
          return (
            <li
              key={c.id}
              onClick={() => onToggle(c)}
              className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-p-1 tw-px-2"
            >
              <Checkbox
                id={id}
                checked={checked}
                disabled={disabled}
                readOnly
                aria-label={c.name}
              />
              <CountryFlag code={c.code} />
              <label htmlFor={id} className="tw-mb-0 tw-flex-1">
                {c.name}
              </label>
              <span className="tw-ml-auto tw-inline-block tw-flex-none tw-shrink-0 tw-rounded tw-bg-gray-100 tw-px-1.5 tw-py-0.5 tw-text-xs tw-text-gray-500">
                {c.code}
              </span>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
